import * as React from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core';

// Replaces InfoPanel.tsx (Semantic UI List/List.Item) for the Parties redesign only —
// InfoPanel itself stays untouched since it's still shared by ClientAndContactDetails,
// MatterSummaryCard, FinancialsInfo, etc. Semantic's own `.ui.list > .item` line-height was
// being forced to LineHeight.Large (40px) by OrganisationWrapper/ContactListWrapper's
// `.list .item` override, inflating every row (BorderedInfoTable row, PartyContactRow) far
// past its own compact padding — this plain div stack has no such inherited override.
//
// No gap of its own between items — PartyContactRow/BorderedInfoTable already own their row
// spacing, so adding one here double-spaces the phone/email/address rows inside a single card.
// Card-to-card spacing (multiple contacts/organisations under one relationship) is handled by
// PartyInfoTable.tsx instead, the only place that actually stacks whole cards.
export interface PartyInfoPanelProps {
    infoItems: Array<{}>;
}

export const PartyInfoPanel: React.FC<PartyInfoPanelProps> = ({ infoItems }) => (
    <div>
        {infoItems.map((infoItem, idx) => (
            <div key={idx}>{infoItem}</div>
        ))}
    </div>
);

const useCardStyles = makeStyles((theme: Theme) =>
    createStyles({
        card: {
            width: '100%',
            boxSizing: 'border-box',
            background: '#fff',
            boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: 8,
            padding: theme.spacing(2.5),
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            marginBottom: theme.spacing(2),
        },
        // Explicit bug report: this card is used both for the top-level party in a
        // relationship and for nested "related party" cards (see PartyCommon.tsx's
        // relatedParties handling) - at 44px/15.5px-700 it read bigger and heavier than the
        // accordion's own panel heading above it (Parties.tsx's PartyDescriptionText, 14px/600)
        // despite being nested content, not the primary heading. Sized down to match.
        iconChip: {
            width: 36,
            height: 36,
            minWidth: 36,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            backgroundColor: 'var(--rv-accent-indigo-bg)',
            color: 'var(--rv-accent-indigo)',
            '& svg': {
                fontSize: 19,
            },
        },
        name: {
            fontSize: 14,
            fontWeight: 600,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
        },
    })
);

export interface PartyCardProps {
    icon: React.ReactNode;
    /** Omit to hide the header entirely — matches the old contactHeader's
     * parentTitle === data.displayName suppression in ContactTable.tsx/OrganizationTable.tsx. */
    title?: React.ReactNode;
    className?: string;
    children: React.ReactNode;
}

// Same card chrome as StaffCard.tsx (icon chip + bold name header, rounded border/shadow) —
// gives the Parties tab's contact/organisation cards the same modern look already established
// one tab over, replacing the old flat grey ContactHeader bar.
export const PartyCard: React.FC<PartyCardProps> = ({ icon, title, className, children }) => {
    const classes = useCardStyles();

    return (
        <div className={className ? `${classes.card} ${className}` : classes.card}>
            {title && (
                <div className={classes.header}>
                    <span className={classes.iconChip}>{icon}</span>
                    <div className={classes.name}>{title}</div>
                </div>
            )}
            {children}
        </div>
    );
};
